import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import type { PanelSpan } from '@/components/shared';
import { OrganizationDetailPanel } from './OrganizationDetailPanelGrid';
import { useHostnameDefaults } from '../useHostnameDefaults';
import { EditHostnameDefaultsModal } from './EditHostnameDefaultsModal';

export function OrganizationHostnameDefaultsPanel({
  organizationId,
  currentSpan,
  onUpdated,
}: {
  organizationId: string;
  currentSpan: PanelSpan;
  onUpdated: () => void;
}) {
  const defaults = useHostnameDefaults(organizationId);
  const [open, setOpen] = useState(false);
  const canEdit =
    defaults.data && Object.values(defaults.data).some((line) => line.options.length > 1);
  return (
    <>
      <OrganizationDetailPanel
        className="[&_[data-tile-header-actions]]:opacity-100 [&_.tile-drag-header_h2]:pr-10"
        title="Default interview hostnames"
        panelId="organization-interview-hostnames"
        span="2x1"
        currentSpan={currentSpan}
        headerActions={
          <button
            type="button"
            aria-label="Default interview hostname settings"
            title="Default interview hostname settings"
            disabled={!canEdit}
            onClick={() => setOpen(true)}
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-backoffice-50 hover:text-neutral-700 disabled:opacity-40"
          >
            <SlidersHorizontal className="size-4" aria-hidden="true" />
          </button>
        }
      >
        {defaults.isPending ? (
          <p>Loading interview hostnames...</p>
        ) : defaults.isError ? (
          <p role="status">Interview hostname defaults are temporarily unavailable</p>
        ) : defaults.data ? (
          <dl className="space-y-2 text-small">
            <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
              <dt className="font-semibold">Insights</dt>
              <dd className="break-all text-neutral-700">{defaults.data.insights.hostname}</dd>
            </div>
            <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
              <dt className="font-semibold">Customer Success</dt>
              <dd className="break-all text-neutral-700">
                {defaults.data.customer_success.hostname}
              </dd>
            </div>
          </dl>
        ) : null}
      </OrganizationDetailPanel>
      {open && defaults.data ? (
        <EditHostnameDefaultsModal
          defaults={defaults.data}
          onClose={() => setOpen(false)}
          onSave={async (values) => {
            await defaults.update.mutateAsync(values);
            onUpdated();
          }}
        />
      ) : null}
    </>
  );
}
